import { TableProps, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { boldColValueRenderer } from '../../../foundation/utils/boldColValueRenderer';
import { useGetMolecularImpactHeaderDescriptionsQuery } from '../../api/globalDataApi';
import { useGetVariantMolecularImpactHeaderQuery } from '../../api/variantApi';
import { MolecularImpactHeaderType } from '../redux';

const MolecularImpactHeader = () => {
  const params = useParams();
  // Read the molecular-impact header straight from the query hook's cache
  const {
    data: molecularImpactRecord,
    isLoading: isMolecularImpactHeaderLoading,
  } = useGetVariantMolecularImpactHeaderQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  // The backend returns a single object; wrap into an array only here where
  // Ant Design's Table requires an array dataSource.
  const molecularImpactHeader = molecularImpactRecord
    ? [molecularImpactRecord]
    : undefined;

  const { data: molecularImpactHeaderDescriptions = [] } =
    useGetMolecularImpactHeaderDescriptionsQuery();

  const columns: TableProps<MolecularImpactHeaderType>['columns'] =
    useMemo(() => {
      const cols = [
        {
          title: 'Calculated consequence',
          dataIndex: 'calculated_consequence',
          key: 'calculated_consequence',
          render: (value: string) => {
            const descriptionObject = molecularImpactHeaderDescriptions.find(
              (consequence) => consequence.calculated_consequence === value,
            );

            return (
              <Tooltip
                title={descriptionObject?.consequence_description}
                overlayClassName="molecular-impact__tooltip"
                placement="bottom"
              >
                <span className="molecular-impact__value">{value}</span>
              </Tooltip>
            );
          },
        },
        {
          title: 'Impact',
          dataIndex: 'impact',
          key: 'impact',
          render: (value: string) => {
            const descriptionObject = molecularImpactHeaderDescriptions.find(
              (impactDescription) =>
                impactDescription.impact === value?.toLocaleLowerCase(),
            );

            return (
              <Tooltip
                placement="bottom"
                title={descriptionObject?.impact_description}
                overlayClassName="molecular-impact__tooltip"
              >
                <span className="molecular-impact__value">{value}</span>
              </Tooltip>
            );
          },
        },
        {
          title: 'Chromosome',
          dataIndex: 'chromosome',
          key: 'chromosome',
          render: boldColValueRenderer,
        },
        {
          title: 'Affected exon',
          dataIndex: 'affected_exon',
          key: 'affected_exon',
          render: boldColValueRenderer,
        },
      ];

      if (
        molecularImpactRecord?.nonsense_mediated_decay ||
        molecularImpactRecord?.nonsense_mediated_decay !== null
      ) {
        cols.push({
          title: 'Nonsense mediated decay',
          dataIndex: 'nonsense_mediated_decay',
          key: 'nonsense_mediated_decay',
          render: boldColValueRenderer,
        });
      }

      return cols;
    }, [molecularImpactHeader, molecularImpactHeaderDescriptions]);

  return (
    <div>
      <ResizableTable
        columns={columns}
        dataSource={molecularImpactHeader}
        pagination={false}
        rowKey={(record) => record.calculated_consequence}
        loading={isMolecularImpactHeaderLoading}
        persistenceKey="molecular-impact-header"
      />
    </div>
  );
};

export default MolecularImpactHeader;
